import { useMemo, useState, useRef } from 'react';
import { Alert, Box, Card, CardContent, Chip, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DarkUpperSection, PageSection, StyledButton } from '../../components';
import {
  confirmAttachmentRequest,
  createQmRecordRequest,
  getQmRecordRequest,
  getUploadUrlRequest,
  listAttachmentsRequest,
  QmAttachment,
  QmCrudStatus,
  QmRecordType,
  QmRecord,
  updateQmRecordRequest,
  uploadFileToS3
} from '../../api/qmCrud';
import { useCurrentUser } from '../../hooks';
import { useUserDetails } from '../../contexts/UserDetailsContext';
import { SUPER_ADMINS, UAT_SUPER_ADMINS } from '../../common/constants';
import { Navigate } from 'react-router-dom';

type CreateFormState = {
  type: QmRecordType;
  reasonCode: string;
  poNumber: string;
  poLine: string;
  poLineId: string;
  itemId: string;
  itemLocation: string;
  sscc: string;
  description: string;
};

type ReadFormState = {
  qmId: string;
  qmNumber: string;
};

type UpdateFormState = {
  qmId: string;
  status: QmCrudStatus;
  closureNotes: string;
  note: string;
};

const defaultCreateForm: CreateFormState = {
  type: 'INBOUND',
  reasonCode: 'INCORRECT_PACKAGING',
  poNumber: '4501234567',
  poLine: '',
  poLineId: '',
  itemId: '',
  itemLocation: 'PER',
  sscc: '',
  description: 'QM test record created from local dev page'
};

const defaultReadForm: ReadFormState = {
  qmId: '',
  qmNumber: ''
};

const defaultUpdateForm: UpdateFormState = {
  qmId: '',
  status: 'PENDING',
  closureNotes: '',
  note: 'Local dev QM test update'
};

function parseError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Unknown error';
}

export default function QmTestPage() {
  const user = useCurrentUser();
  const { userDetails } = useUserDetails();
  const isSuperAdmin = useMemo(() => {
    const superAdminList = process.env.REACT_APP_IS_PROD === 'true' ? SUPER_ADMINS : UAT_SUPER_ADMINS;

    return superAdminList.map((sa) => sa.toLowerCase()).includes(user.email.toLowerCase());
  }, [user.email]);
  const [createForm, setCreateForm] = useState<CreateFormState>(defaultCreateForm);
  const [readForm, setReadForm] = useState<ReadFormState>(defaultReadForm);
  const [updateForm, setUpdateForm] = useState<UpdateFormState>(defaultUpdateForm);
  const [lastResult, setLastResult] = useState<QmRecord | null>(null);
  const [lastError, setLastError] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [attachmentQmId, setAttachmentQmId] = useState<string>('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<QmAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isListingAttachments, setIsListingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const qmListRole = userDetails?.qmListRole || 'QM_LIST_NO_ACCESS';
  const hasQmAccess = qmListRole !== 'QM_LIST_NO_ACCESS';
  const canEditQmRecords = qmListRole === 'QM_LIST_EDITABLE';

  if (!isSuperAdmin) {
    return <Navigate to="/settings" replace />;
  }

  async function handleCreate() {
    if (!hasQmAccess) {
      setLastError('You do not have QM access.');
      return;
    }

    if (!canEditQmRecords) {
      setLastError('Your QM role is read-only. Create is disabled.');
      return;
    }

    setIsCreating(true);
    setLastError('');

    try {
      const result = await createQmRecordRequest({
        type: createForm.type,
        reasonCode: createForm.reasonCode,
        poNumber: createForm.poNumber.trim() || null,
        poLine: createForm.poLine.trim() || null,
        poLineId: createForm.poLineId.trim() || null,
        itemId: createForm.itemId.trim() || null,
        itemLocation: createForm.itemLocation,
        sscc: createForm.sscc || null,
        description: createForm.description || null,
        createdBy: user.email || null
      });

      setLastResult(result);
      setReadForm({
        qmId: String(result.qmId),
        qmNumber: result.qmNumber
      });
      setUpdateForm((current) => ({
        ...current,
        qmId: String(result.qmId)
      }));
    } catch (error) {
      setLastError(parseError(error));
    } finally {
      setIsCreating(false);
    }
  }

  async function handleRead() {
    if (!hasQmAccess) {
      setLastError('You do not have QM access.');
      return;
    }

    setIsReading(true);
    setLastError('');

    try {
      const result = await getQmRecordRequest({
        qmId: readForm.qmId ? Number(readForm.qmId) : undefined,
        qmNumber: readForm.qmNumber || undefined
      });

      if (!result) {
        setLastError('QM record not found');
        setLastResult(null);
        return;
      }

      setLastResult(result);
      setUpdateForm((current) => ({
        ...current,
        qmId: String(result.qmId)
      }));
      setAttachmentQmId(String(result.qmId));
    } catch (error) {
      setLastError(parseError(error));
    } finally {
      setIsReading(false);
    }
  }

  async function handleUpdate() {
    if (!hasQmAccess) {
      setLastError('You do not have QM access.');
      return;
    }

    if (!canEditQmRecords) {
      setLastError('Your QM role is read-only. Update is disabled.');
      return;
    }

    setIsUpdating(true);
    setLastError('');

    try {
      const result = await updateQmRecordRequest({
        qmId: Number(updateForm.qmId),
        status: updateForm.status,
        closureNotes: updateForm.closureNotes || null,
        note: updateForm.note || null,
        updatedBy: user.email || null
      });

      setLastResult(result);
      setReadForm({
        qmId: String(result.qmId),
        qmNumber: result.qmNumber
      });
      setAttachmentQmId(String(result.qmId));
    } catch (error) {
      setLastError(parseError(error));
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleUploadAttachment() {
    if (!attachmentFile || !attachmentQmId) return;
    if (!hasQmAccess) {
      setAttachmentError('You do not have QM access.');
      return;
    }

    if (!canEditQmRecords) {
      setAttachmentError('Your QM role is read-only. Attachment upload is disabled.');
      return;
    }

    setIsUploading(true);
    setAttachmentError('');

    try {
      const { uploadUrl, s3Key } = await getUploadUrlRequest({
        qmId: Number(attachmentQmId),
        fileName: attachmentFile.name,
        mimeType: attachmentFile.type || null
      });

      await uploadFileToS3(uploadUrl, attachmentFile);

      const confirmed = await confirmAttachmentRequest({
        qmId: Number(attachmentQmId),
        s3Key,
        fileName: attachmentFile.name,
        mimeType: attachmentFile.type || null,
        sizeBytes: attachmentFile.size,
        uploadedBy: user.email || null
      });

      setAttachments((current) => [confirmed, ...current]);
      setAttachmentFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      setAttachmentError(parseError(error));
    } finally {
      setIsUploading(false);
    }
  }

  async function handleListAttachments() {
    if (!attachmentQmId) return;
    if (!hasQmAccess) {
      setAttachmentError('You do not have QM access.');
      return;
    }

    setIsListingAttachments(true);
    setAttachmentError('');

    try {
      const results = await listAttachmentsRequest(Number(attachmentQmId));
      setAttachments(results);
    } catch (error) {
      setAttachmentError(parseError(error));
    } finally {
      setIsListingAttachments(false);
    }
  }

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={1} sx={{ padding: '1em 0' }}>
          <Typography variant="h2" fontWeight="bold">
            QM Test Page
          </Typography>
          <Typography variant="body1">
            Manual non-prod browser test for QM create, read, and update against the deployed API.
          </Typography>
        </Stack>
      </DarkUpperSection>
      <PageSection>
        <Stack spacing={3} sx={{ padding: '1.5em 0', maxWidth: 960, margin: '0 auto' }}>
          <Alert severity="info">
            Use this page to confirm AppSync resolver wiring and Dev PostgreSQL writes. Start with create, then read,
            then update.
          </Alert>
          <Alert severity={canEditQmRecords ? 'success' : hasQmAccess ? 'warning' : 'error'}>
            QM role: {qmListRole}.{' '}
            {canEditQmRecords
              ? 'Create/update/upload are enabled.'
              : hasQmAccess
                ? 'Read-only access is enabled.'
                : 'No QM access is enabled.'}
          </Alert>

          {lastError ? <Alert severity="error">{lastError}</Alert> : null}

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">Create QM Record</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    fullWidth
                    label="Type"
                    value={createForm.type}
                    onChange={(event) =>
                      setCreateForm((current) => ({
                        ...current,
                        type: event.target.value as QmRecordType
                      }))
                    }>
                    <MenuItem value="INBOUND">INBOUND</MenuItem>
                    <MenuItem value="OUTBOUND">OUTBOUND</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    label="Reason Code"
                    value={createForm.reasonCode}
                    onChange={(event) => setCreateForm((current) => ({ ...current, reasonCode: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="PO Number"
                    value={createForm.poNumber}
                    onChange={(event) => setCreateForm((current) => ({ ...current, poNumber: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="Item Location"
                    value={createForm.itemLocation}
                    onChange={(event) => setCreateForm((current) => ({ ...current, itemLocation: event.target.value }))}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="SSCC"
                    value={createForm.sscc}
                    onChange={(event) => setCreateForm((current) => ({ ...current, sscc: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="PO Line"
                    value={createForm.poLine}
                    onChange={(event) => setCreateForm((current) => ({ ...current, poLine: event.target.value }))}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="PO Line ID"
                    value={createForm.poLineId}
                    onChange={(event) => setCreateForm((current) => ({ ...current, poLineId: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="Item ID"
                    value={createForm.itemId}
                    onChange={(event) => setCreateForm((current) => ({ ...current, itemId: event.target.value }))}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={createForm.description}
                    onChange={(event) => setCreateForm((current) => ({ ...current, description: event.target.value }))}
                  />
                </Stack>
                <Box>
                  <StyledButton variant="contained" onClick={handleCreate} disabled={isCreating || !canEditQmRecords}>
                    {isCreating ? 'Creating...' : 'Create Record'}
                  </StyledButton>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">Read QM Record</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="QM ID"
                    value={readForm.qmId}
                    onChange={(event) => setReadForm((current) => ({ ...current, qmId: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="QM Number"
                    value={readForm.qmNumber}
                    onChange={(event) => setReadForm((current) => ({ ...current, qmNumber: event.target.value }))}
                  />
                </Stack>
                <Box>
                  <StyledButton variant="contained" onClick={handleRead} disabled={isReading || !hasQmAccess}>
                    {isReading ? 'Reading...' : 'Read Record'}
                  </StyledButton>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">Update QM Record</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="QM ID"
                    value={updateForm.qmId}
                    onChange={(event) => setUpdateForm((current) => ({ ...current, qmId: event.target.value }))}
                  />
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={updateForm.status}
                    onChange={(event) =>
                      setUpdateForm((current) => ({
                        ...current,
                        status: event.target.value as QmCrudStatus
                      }))
                    }>
                    <MenuItem value="CREATED">CREATED</MenuItem>
                    <MenuItem value="PENDING">PENDING</MenuItem>
                    <MenuItem value="CLOSED">CLOSED</MenuItem>
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Closure Notes"
                    value={updateForm.closureNotes}
                    onChange={(event) => setUpdateForm((current) => ({ ...current, closureNotes: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="History Note"
                    value={updateForm.note}
                    onChange={(event) => setUpdateForm((current) => ({ ...current, note: event.target.value }))}
                  />
                </Stack>
                <Box>
                  <StyledButton
                    variant="contained"
                    onClick={handleUpdate}
                    disabled={isUpdating || !updateForm.qmId || !canEditQmRecords}>
                    {isUpdating ? 'Updating...' : 'Update Record'}
                  </StyledButton>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Divider />

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">Attachments</Typography>
                {attachmentError ? <Alert severity="error">{attachmentError}</Alert> : null}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                  <TextField
                    fullWidth
                    label="QM ID"
                    value={attachmentQmId}
                    onChange={(event) => setAttachmentQmId(event.target.value)}
                  />
                  <StyledButton
                    variant="outlined"
                    onClick={handleListAttachments}
                    disabled={isListingAttachments || !attachmentQmId || !hasQmAccess}>
                    {isListingAttachments ? 'Loading...' : 'List Attachments'}
                  </StyledButton>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={(event) => setAttachmentFile(event.target.files?.[0] ?? null)}
                      style={{ width: '100%' }}
                    />
                  </Box>
                  <StyledButton
                    variant="contained"
                    onClick={handleUploadAttachment}
                    disabled={isUploading || !attachmentFile || !attachmentQmId || !canEditQmRecords}>
                    {isUploading ? 'Uploading...' : 'Upload File'}
                  </StyledButton>
                </Stack>
                {attachments.length > 0 ? (
                  <Stack spacing={1}>
                    {attachments.map((a) => (
                      <Box
                        key={a.id}
                        sx={{
                          padding: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flexWrap: 'wrap'
                        }}>
                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                          {a.fileName}
                        </Typography>
                        {a.mimeType ? <Chip label={a.mimeType} size="small" /> : null}
                        {a.sizeBytes ? (
                          <Typography variant="caption" color="text.secondary">
                            {(a.sizeBytes / 1024).toFixed(1)} KB
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary">
                          {a.uploadedBy} · {new Date(a.uploadedAt).toLocaleString()}
                        </Typography>
                        <StyledButton variant="text" size="small" onClick={() => window.open(a.downloadUrl, '_blank')}>
                          Download
                        </StyledButton>
                      </Box>
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <Divider />

          <Card>
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h6">Last Result</Typography>
                <Typography variant="body2" color="text.secondary">
                  Signed in as {user.email || 'unknown user'}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    margin: 0,
                    padding: 2,
                    overflow: 'auto',
                    backgroundColor: 'grey.100',
                    borderRadius: 1,
                    fontSize: 12
                  }}>
                  {JSON.stringify(lastResult, null, 2)}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </PageSection>
    </>
  );
}
